import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import NewCustomerDrawer from "@/components/NewCustomerDrawer";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, EnvelopeSimple, Eye, ArrowUpRight, User, Plus, Trash } from "@phosphor-icons/react";

export default function AdminCustomers() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/customers", { params: query ? { q: query } : {} });
      setRows(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to load customers");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(q.trim());
  };

  const sendReset = async (uid, email) => {
    if (!window.confirm(`Send a password-reset link to ${email}?`)) return;
    setBusyId(uid + ":reset");
    try {
      await api.post(`/admin/customers/${uid}/send-reset`);
      toast.success(`Reset link emailed to ${email}`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to send reset link");
    } finally { setBusyId(""); }
  };

  const impersonate = async (uid, name) => {
    if (!window.confirm(`Sign in as ${name}? You'll see exactly what they see. Click "Return to admin" in the top banner to come back.`)) return;
    setBusyId(uid + ":imp");
    try {
      const { data } = await api.post(`/admin/customers/${uid}/impersonate`);
      // Refetch /me so AuthContext picks up impersonation flags and role change
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      toast.success(`Now viewing as ${data.customer.first_name} ${data.customer.last_name}`);
      nav("/dashboard");
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to impersonate");
    } finally { setBusyId(""); }
  };

  const deleteCustomer = async (c, name) => {
    const jc = c.job_count || 0;
    const detail = jc > 0
      ? `This will also PERMANENTLY DELETE ${jc} job${jc === 1 ? "" : "s"} and all associated comments/messages.`
      : `This customer has no jobs.`;
    const confirm = window.prompt(`PERMANENTLY DELETE ${name} (${c.email})?\n\n${detail}\n\nThis cannot be undone. Type DELETE to confirm.`, "");
    if (confirm !== "DELETE") return;
    setBusyId(c.id + ":del");
    try {
      const { data } = await api.delete(`/admin/customers/${c.id}`);
      toast.success(`Deleted ${name}${data.deleted_jobs ? ` (and ${data.deleted_jobs} job${data.deleted_jobs === 1 ? "" : "s"})` : ""}`);
      await load(q.trim());
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Failed to delete customer");
    } finally { setBusyId(""); }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">Admin · Customers</div>
            <h1 className="font-display text-5xl md:text-6xl">Customer management</h1>
            <p className="text-[var(--gc-text-muted)] mt-3 max-w-xl">
              Search, send password reset links, or sign in as any customer to see the portal from their point of view.
            </p>
          </div>
          <form onSubmit={onSearch} className="flex items-center gap-2" data-testid="customers-search-form">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gc-text-muted)]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, email or business…"
                className="gc-input pl-9 w-80"
                data-testid="customers-search-input"
              />
            </div>
            <button type="submit" className="border border-[var(--gc-border)] hover:border-[var(--gc-navy)] px-4 py-2 text-sm" data-testid="customers-search-btn">Search</button>
            {q && (
              <button
                type="button"
                onClick={() => { setQ(""); load(""); }}
                className="text-xs text-[var(--gc-text-muted)] link-underline"
                data-testid="customers-clear-btn"
              >Clear</button>
            )}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="btn-solid ml-2"
              data-testid="new-customer-btn"
            >
              <Plus size={16} weight="bold" /> New customer
            </button>
          </form>
        </div>

        <div className="border border-[var(--gc-border)]">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[var(--gc-border)] bg-[var(--gc-bg-alt)] eyebrow">
            <div className="col-span-3">Customer</div>
            <div className="col-span-3">Business</div>
            <div className="col-span-2">Contact</div>
            <div className="col-span-1 text-right">Jobs</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>

          {loading && (
            <div className="p-10 text-center text-[var(--gc-text-muted)]" data-testid="customers-loading">Loading customers…</div>
          )}

          {!loading && rows.length === 0 && (
            <div className="p-10 text-center text-[var(--gc-text-muted)]" data-testid="customers-empty">
              No customers found{q ? ` for “${q}”` : ""}.
            </div>
          )}

          {!loading && rows.map((c) => {
            const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || "—";
            return (
              <div
                key={c.id}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[var(--gc-border)] items-center hover:bg-[var(--gc-bg-alt)] transition-colors"
                data-testid={`customer-row-${c.id}`}
              >
                <div className="col-span-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-[var(--gc-navy)] text-white flex items-center justify-center shrink-0">
                      <User size={16} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{name}</div>
                      <div className="text-xs text-[var(--gc-text-muted)] font-mono-tech truncate">{c.county}</div>
                    </div>
                  </div>
                </div>
                <div className="col-span-3 truncate">
                  <div className="text-sm font-semibold truncate">{c.business_name || "—"}</div>
                  <div className="text-xs text-[var(--gc-text-muted)] truncate">{c.vat_number || ""}</div>
                </div>
                <div className="col-span-2 truncate">
                  <div className="text-sm truncate">{c.email}</div>
                  <div className="text-xs text-[var(--gc-text-muted)] truncate">{c.phone}</div>
                </div>
                <div className="col-span-1 text-right font-display text-2xl">{c.job_count}</div>
                <div className="col-span-3 flex items-center justify-end gap-2 flex-wrap">
                  <button
                    onClick={() => sendReset(c.id, c.email)}
                    disabled={busyId === c.id + ":reset"}
                    className="border border-[var(--gc-border)] hover:border-[var(--gc-orange)] hover:text-[var(--gc-orange)] px-2.5 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                    title="Send password reset link"
                    data-testid={`customer-send-reset-${c.id}`}
                  >
                    <EnvelopeSimple size={14} weight="bold" />
                    Reset
                  </button>
                  <button
                    onClick={() => impersonate(c.id, name)}
                    disabled={busyId === c.id + ":imp"}
                    className="btn-solid px-3 py-2 text-xs whitespace-nowrap disabled:opacity-50"
                    title="Sign in as this customer"
                    data-testid={`customer-impersonate-${c.id}`}
                  >
                    <Eye size={14} weight="bold" />
                    View&nbsp;as
                    <ArrowUpRight size={12} weight="bold" />
                  </button>
                  <button
                    onClick={() => deleteCustomer(c, name)}
                    disabled={busyId === c.id + ":del"}
                    className="border border-[var(--gc-danger)] text-[var(--gc-danger)] hover:bg-red-50 px-2.5 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                    title="Permanently delete this customer and all their jobs"
                    data-testid={`customer-delete-${c.id}`}
                  >
                    <Trash size={14} weight="bold" />
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <NewCustomerDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={() => load(q.trim())}
      />
    </Layout>
  );
}
